import { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Share,
  QrCode2,
  Flag,
  Phone,
  Email,
  Language,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { SaveBookmarkIcon, SaveBookmarkOutlineIcon } from '../components/icons/SaveBookmarkIcon';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import { openAddressInMaps } from '../utils/openInMaps';
import { buildContactLinks, ContactLink } from '../utils/contactLinks';
import { buildPhoneActions, PhoneAction } from '../utils/phoneActions';
import { formatDistanceKmAway } from '../utils/distanceDisplay';
import { displayFolderName } from '../utils/folderDisplayName';
import { useT } from '../i18n/useT';
import Sms from '@mui/icons-material/Sms';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ListItemIcon from '@mui/material/ListItemIcon';
import {
  fetchSingleBusiness,
  fetchRelatedBusinesses,
  fetchSubbusinesses,
  getBusinessLogoUrl,
  getBusinessCoverUrl,
  ApiBusiness,
  fetchReportReasons,
  reportBusiness,
  ReportReason,
  fetchUserFolders,
  addBusinessToFolder,
  removeBusinessFromAllFolders,
  ApiFolder,
} from '../services/api';
import { getBusinessVideoUrl, getSocialIconUrl } from '../utils/assetUrls';
import { publicProfileUrl } from '../utils/publicProfileUrl';
import PlayArrow from '@mui/icons-material/PlayArrow';

function ContactGlyph({ typeName, kind, icon }: { typeName: string; kind: string; icon?: string }) {
  const [imgFailed, setImgFailed] = useState(false);
  const iconUrl = getSocialIconUrl(icon);
  const t = typeName.toLowerCase();

  if (iconUrl && !imgFailed) {
    return (
      <img
        src={iconUrl}
        alt=""
        style={{ width: 22, height: 22, objectFit: 'contain' }}
        onError={() => setImgFailed(true)}
      />
    );
  }
  if (kind === 'phone' || t.includes('phone') || t.includes('whatsapp') || t.includes('twint')) {
    return <Phone sx={{ fontSize: 22 }} />;
  }
  if (kind === 'email' || t.includes('email') || t.includes('mail')) {
    return <Email sx={{ fontSize: 22 }} />;
  }
  if (kind === 'website' || t.includes('website') || t.includes('web')) {
    return <Language sx={{ fontSize: 22 }} />;
  }
  if (t.includes('telegram')) {
    return <img src="/images/icons/telegram.svg" alt="" style={{ width: 22, height: 22 }} />;
  }
  if (t.includes('instagram')) {
    return <img src="/images/icons/instagram.svg" alt="" style={{ width: 22, height: 22 }} />;
  }
  if (t.includes('linkedin')) {
    return <img src="/images/icons/linkedin.svg" alt="" style={{ width: 22, height: 22 }} />;
  }
  if (t.includes('bandcamp')) {
    return <img src="/images/icons/bandcamp.svg" alt="" style={{ width: 22, height: 22 }} />;
  }
  return <LinkIcon sx={{ fontSize: 22 }} />;
}

function contactIcon(typeName: string, kind: string, icon?: string) {
  return <ContactGlyph typeName={typeName} kind={kind} icon={icon} />;
}

function actionBtnSx() {
  return {
    width: 44,
    height: 44,
    borderRadius: '50%',
    bgcolor: DesignSystem.colors.surface,
    color: DesignSystem.colors.textSecondary,
    border: `1px solid ${DesignSystem.colors.separator}`,
    '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated },
  };
}

export default function BusinessProfilePage() {
  const navigate = useNavigate();
  const { id, username: profilePathUsername } = useParams<{ id?: string; username?: string }>();
  const routeUsername = profilePathUsername ?? id;
  const { user } = useAuth();
  const t = useT();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [business, setBusiness] = useState<ApiBusiness | null>(null);
  const [relatedBusinesses, setRelatedBusinesses] = useState<ApiBusiness[]>([]);
  const [samePersonBusinesses, setSamePersonBusinesses] = useState<ApiBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedTab, setRelatedTab] = useState<'recommended' | 'same_person'>('recommended');
  const [reportOpen, setReportOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [reasons, setReasons] = useState<ReportReason[]>([]);
  const [reasonId, setReasonId] = useState('');
  const [reportMsg, setReportMsg] = useState('');
  const [reportErr, setReportErr] = useState<string | null>(null);
  const [reportOk, setReportOk] = useState<string | null>(null);
  const [folders, setFolders] = useState<ApiFolder[]>([]);
  const [folderErr, setFolderErr] = useState<string | null>(null);
  const [folderOk, setFolderOk] = useState<string | null>(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [contactSheet, setContactSheet] = useState<{
    title: string;
    subtitle?: string;
    actions: Array<{ id: string; label: string; href?: string; copyText?: string }>;
  } | null>(null);
  const [copyHint, setCopyHint] = useState(false);

  const openHref = (href: string) => {
    // Prefer navigation so iOS Safari shows native Call / Mail / Open-with prompts.
    window.location.href = href;
  };

  const openContactSheetFor = (link: ContactLink) => {
    if (link.kind === 'phone') {
      const phoneActions: PhoneAction[] = buildPhoneActions(link.label, link.phoneExtras);
      setContactSheet({
        title: t('selectAction'),
        subtitle: link.label,
        actions: [
          ...phoneActions.map((a) => ({
            id: a.id,
            label:
              a.id === 'call'
                ? t('directCall')
                : a.id === 'sms'
                  ? t('sendSms')
                  : a.label,
            href: a.href,
          })),
          { id: 'copy', label: t('copyValue'), copyText: link.label },
        ],
      });
      return;
    }
    if (link.kind === 'email') {
      setContactSheet({
        title: t('selectAction'),
        subtitle: link.label,
        actions: [
          { id: 'email', label: t('openEmail'), href: link.href },
          { id: 'copy', label: t('copyValue'), copyText: link.label },
        ],
      });
      return;
    }
    if (link.kind === 'website') {
      setContactSheet({
        title: t('selectAction'),
        subtitle: link.label,
        actions: [
          { id: 'web', label: t('openWebsite'), href: link.href },
          { id: 'copy', label: t('copyValue'), copyText: link.href },
        ],
      });
      return;
    }
    // Social / other pages — confirm before leaving (iOS parity)
    setContactSheet({
      title: t('openLink'),
      subtitle: link.typeName,
      actions: [
        { id: 'open', label: t('openLink'), href: link.href },
        { id: 'copy', label: t('copyValue'), copyText: link.href },
      ],
    });
  };

  const actionIcon = (id: string) => {
    if (id === 'call') return <Phone sx={{ fontSize: 22 }} />;
    if (id === 'sms') return <Sms sx={{ fontSize: 22 }} />;
    if (id === 'email') return <Email sx={{ fontSize: 22 }} />;
    if (id === 'web' || id === 'open') return <Language sx={{ fontSize: 22 }} />;
    if (id === 'copy') return <ContentCopy sx={{ fontSize: 22 }} />;
    return <LinkIcon sx={{ fontSize: 22 }} />;
  };

  const openFolderPicker = async () => {
              if (!user?.token) {
      navigate('/?login=1');
      return;
    }
    setFolderErr(null);
    setFolderOk(null);
    try {
      const res = await fetchUserFolders(user.token, getApiLanguage());
      setFolders(res.folders || []);
      setFolderOpen(true);
    } catch (e) {
      setFolderErr(e instanceof Error ? e.message : t('couldNotLoadLists'));
      setFolderOpen(true);
    }
  };

  const handleBookmarkAction = async () => {
    if (!business) return;
              if (!user?.token) {
      navigate('/?login=1');
      return;
    }
    if (isBookmarked) {
      setActionBusy(true);
      try {
        await removeBusinessFromAllFolders(business.id, user.token, getApiLanguage());
        setIsBookmarked(false);
      } catch (e) {
        console.error('Error removing bookmark:', e);
      } finally {
        setActionBusy(false);
      }
      return;
    }
    await openFolderPicker();
  };

  useEffect(() => {
    const loadBusiness = async () => {
      if (!routeUsername) {
        setError(t('businessIdRequired'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setRelatedBusinesses([]);
        setSamePersonBusinesses([]);

        const username = routeUsername.replace('@', '').replace('~', '');

        let response = await fetchSingleBusiness(username);
        if (user?.token) {
          try {
            const authed = await fetchSingleBusiness(username, user.token);
            if (authed?.business) {
              response = authed;
            }
          } catch {
            /* keep public response */
          }
        }
        
        // Check if business exists in response
        if (!response || !response.business) {
          throw new Error('Business not found or response invalid');
        }
        
        const businessData = response.business;
        setBusiness(businessData);
        setIsBookmarked((businessData.favorite ?? 0) === 1);
        setMediaIndex(0);
        setVideoOpen(false);
        setVideoError(false);

        // Fetch related businesses if we have the business ID and token
        if (businessData.id && user?.token) {
          let relatedList: ApiBusiness[] = [];
          try {
            const relatedResponse = await fetchRelatedBusinesses(businessData.id, undefined, user.token);
            relatedList = relatedResponse.businesses || [];
            setRelatedBusinesses(relatedList);
          } catch (err) {
            console.warn('Could not fetch related businesses:', err);
            setRelatedBusinesses([]);
          }

          try {
            const subRes = await fetchSubbusinesses(businessData.id, user.token, getApiLanguage());
            const subs = subRes.businesses || [];
            setSamePersonBusinesses(
              subs.length > 0 ? subs : relatedList.filter((b) => b.id !== businessData.id)
            );
          } catch {
            setSamePersonBusinesses(relatedList.filter((b) => b.id !== businessData.id));
          }
        }
      } catch (err) {
        console.error('Error loading business:', err);
        setError(err instanceof Error ? err.message : t('failedLoadBusiness'));
      } finally {
        setLoading(false);
      }
    };

    loadBusiness();
  }, [routeUsername, user?.token]);

  useEffect(() => {
    if (!videoOpen) setVideoError(false);
  }, [videoOpen]);

  // Get cover image URL
  const getCoverImageUrl = () => {
    if (!business) return '';
    
    if (business.coverStrings && business.coverStrings.split(',').length > 0) {
      const firstCover = business.coverStrings.split(',')[0];
      return getBusinessCoverUrl(firstCover) || '';
    }
    
    return '';
  };

  const coverSlides = (() => {
    if (!business) return [] as string[];
    if (business.covers && business.covers.length > 0) {
      return business.covers
        .map((c) => getBusinessCoverUrl(c.cover))
        .filter((u): u is string => Boolean(u));
    }
    if (business.coverStrings) {
      return business.coverStrings
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((c) => getBusinessCoverUrl(c))
        .filter((u): u is string => Boolean(u));
    }
    return [] as string[];
  })();
  const videoUrl = business?.video ? getBusinessVideoUrl(business.video) : null;

  // Get logo URL
  const getLogoUrl = () => {
    if (!business?.logo) return null;
    return getBusinessLogoUrl(business.logo);
  };

  const contactLinks = business ? buildContactLinks(business) : [];
  const coverImageUrl = getCoverImageUrl();
  const logoUrl = getLogoUrl();
  const profileUrl = routeUsername ? publicProfileUrl(routeUsername) : '';

  const shareProfile = async () => {
    if (!profileUrl) return;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: business?.name, url: profileUrl });
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(profileUrl);
    } catch {
      /* ignore */
    }
  };

  const shellActions = !loading && business ? (
    <IconButton sx={{ color: DesignSystem.colors.textPrimary }} onClick={() => void shareProfile()} aria-label={t('shareProfileAria')}>
      <Share />
    </IconButton>
  ) : undefined;

  const socialLinks = contactLinks.filter((l) => l.kind === 'social');
  const otherContacts = contactLinks.filter((l) => l.kind !== 'social');

  const listRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    width: '100%',
    px: 2,
    py: 1.5,
    borderBottom: `1px solid ${DesignSystem.colors.separator}`,
    textDecoration: 'none',
    color: 'inherit',
    textAlign: 'left' as const,
    background: 'transparent',
    border: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid' as const,
    borderBottomColor: DesignSystem.colors.separator,
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': { backgroundColor: DesignSystem.colors.hover },
    '&:last-of-type': { borderBottom: 0 },
  };

  if (loading) {
    return (
      <AppShell title={t('visiShellTitle')} hideTitle showBackButton maxWidth="feed" showCompanion actions={shellActions}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      </AppShell>
    );
  }

  if (error || !business) {
    return (
      <AppShell title={t('visiShellTitle')} showBackButton maxWidth="feed" showCompanion actions={shellActions}>
        <Alert severity="error">{error || t('businessNotFound')}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          {t('goBack')}
        </Button>
      </AppShell>
    );
  }

  return (
    <AppShell title={business.name} hideTitle showBackButton maxWidth="feed" showCompanion actions={shellActions}>
      {/* Cover / video media */}
      <Box sx={{ position: 'relative', width: '100%', mb: 0 }}>
        <Box
          sx={{
            width: '100%',
            height: 220,
            bgcolor: DesignSystem.colors.surfaceElevated,
            borderRadius: DesignSystem.borderRadius.lg,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {videoUrl && mediaIndex === 0 ? (
            <Box
              component="button"
              type="button"
              onClick={() => setVideoOpen(true)}
              aria-label={t('playVideo')}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'block',
                width: '100%',
                height: '100%',
                backgroundImage: coverSlides[0] ? `url(${coverSlides[0]})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                bgcolor: '#000',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.35)',
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: DesignSystem.colors.action,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayArrow sx={{ fontSize: 36, color: DesignSystem.colors.onPrimary }} />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundImage: (videoUrl ? coverSlides[mediaIndex - 1] : coverSlides[mediaIndex] || coverImageUrl)
                  ? `url(${videoUrl ? coverSlides[mediaIndex - 1] : coverSlides[mediaIndex] || coverImageUrl})`
                  : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
        </Box>
        {(videoUrl ? coverSlides.length + 1 : coverSlides.length) > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1.25 }}>
            {Array.from({ length: videoUrl ? coverSlides.length + 1 : coverSlides.length }).map((_, i) => (
              <Box
                key={i}
                component="button"
                type="button"
                onClick={() => setMediaIndex(i)}
                aria-label={`Media ${i + 1}`}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: i === mediaIndex ? DesignSystem.colors.primary : DesignSystem.colors.surfaceElevated,
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: -8 }}>
        {/* Profile Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <Avatar 
            src={logoUrl || ''}
            alt={business.name}
            sx={{ 
              width: 112,
              height: 112,
              bgcolor: DesignSystem.colors.surfaceElevated,
              border: `4px solid ${
                videoUrl ? DesignSystem.colors.primary : DesignSystem.colors.canvas
              }`,
              boxShadow: 'none',
              mb: 2,
              color: DesignSystem.colors.textPrimary,
              fontWeight: 700,
            }}
          >
            <Typography sx={{ fontSize: 36, fontWeight: 800, color: DesignSystem.colors.textPrimary }}>
              {business.name.charAt(0)}
            </Typography>
          </Avatar>

          <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: DesignSystem.colors.textPrimary, mb: 0.5 }}>
            {business.name}
          </Typography>
          
          {business.username && (
            <Typography sx={{ fontSize: 15, color: DesignSystem.colors.textSecondary, mb: 1 }}>
              {business.username.startsWith('@') ? business.username : `@${business.username}`}
            </Typography>
          )}

          {(business.category || business.subcategory) && (
            <Typography sx={{ 
              color: DesignSystem.colors.textSecondary,
              bgcolor: DesignSystem.colors.surface,
              px: 1.75,
              py: 0.5,
              borderRadius: DesignSystem.borderRadius.pill,
              fontSize: 13,
              fontWeight: 600,
              mb: 2,
            }}>
              {business.subcategory?.name || business.category?.name || ''}
            </Typography>
          )}
          
          {business.description && (
            <Typography sx={{ fontSize: 16, color: DesignSystem.colors.textPrimary, mb: 2, maxWidth: 560, lineHeight: 1.45, fontWeight: 500 }}>
              {business.description}
            </Typography>
          )}
          
          {formatDistanceKmAway(business.distance) && (
            <Typography sx={{ fontSize: 13, color: DesignSystem.colors.primary, fontWeight: 600, mb: 2 }}>
              {formatDistanceKmAway(business.distance)}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2.5 }}>
            <Tooltip title={isBookmarked ? t('saved') : t('saveToList')}>
            <IconButton
              onClick={() => void handleBookmarkAction()}
              disabled={actionBusy}
              sx={{
                ...actionBtnSx(),
                color: isBookmarked ? DesignSystem.colors.primary : DesignSystem.colors.textSecondary,
              }}
              aria-label={isBookmarked ? t('saved') : t('saveToList')}
            >
              {isBookmarked ? <SaveBookmarkIcon /> : <SaveBookmarkOutlineIcon />}
            </IconButton>
            </Tooltip>
            <Tooltip title={t('share')}>
            <IconButton
              onClick={() => void shareProfile()}
              sx={actionBtnSx()}
              aria-label={t('share')}
            >
              <Share />
            </IconButton>
            </Tooltip>
            <Tooltip title={t('qrCode')}>
            <IconButton
              onClick={() => {
                const u = (business.username || '').replace(/^@/, '');
                navigate(`/visi/qr/${u}?name=${encodeURIComponent(business.name || u)}`);
              }}
              sx={actionBtnSx()}
              aria-label={t('qrCodeAria')}
            >
              <QrCode2 />
            </IconButton>
            </Tooltip>
            {user?.token && (
              <Tooltip title={t('report')}>
              <IconButton
                sx={actionBtnSx()}
                aria-label={t('reportAria')}
                onClick={async () => {
                  setReportErr(null);
                  setReportOk(null);
                  try {
                    const list = await fetchReportReasons(user.token!, getApiLanguage());
                    setReasons(list);
                    if (list[0]) setReasonId(String(list[0].id));
                    setReportOpen(true);
                  } catch (e) {
                    setReportErr(e instanceof Error ? e.message : 'Could not load reasons');
                    setReportOpen(true);
                  }
                }}
              >
                <Flag />
              </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box
            sx={{
              width: '100%',
              maxWidth: 480,
              mx: 'auto',
              mb: 3,
              textAlign: 'left',
              bgcolor: DesignSystem.colors.surface,
              borderRadius: DesignSystem.borderRadius.lg,
              overflow: 'hidden',
            }}
          >
            {socialLinks.length > 0 && (
              <>
                <Typography
                  sx={{
                    px: 2,
                    pt: 2,
                    pb: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: DesignSystem.colors.textTertiary,
                  }}
                >
                  {t('socialsSection')}
                </Typography>
                {socialLinks.map((link) => (
                  <Box
                    key={link.key}
                    component="button"
                    type="button"
                    onClick={() => openContactSheetFor(link)}
                    sx={listRowSx}
                  >
                    {contactIcon(link.typeName, link.kind, link.icon)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textTertiary }}>
                        {link.typeName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: DesignSystem.colors.textPrimary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {link.label}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            )}
            {(otherContacts.length > 0 ||
              (business.addresses && business.addresses[0])) && (
              <>
                <Typography
                  sx={{
                    px: 2,
                    pt: socialLinks.length ? 1.5 : 2,
                    pb: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: DesignSystem.colors.textTertiary,
                  }}
                >
                  {t('contactSection')}
                </Typography>
                {otherContacts.map((link) => (
                  <Box
                    key={link.key}
                    component="button"
                    type="button"
                    onClick={() => openContactSheetFor(link)}
                    sx={listRowSx}
                  >
                    {contactIcon(link.typeName, link.kind, link.icon)}
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: DesignSystem.colors.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {link.kind === 'website' ? t('website') : link.label}
                    </Typography>
                  </Box>
                ))}
                {business.addresses && business.addresses[0] && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      const a = business.addresses![0];
                      openAddressInMaps({
                        latitude: a.latitude,
                        longitude: a.longitude,
                        address: a.address,
                        city: a.city,
                        country: a.country,
                      });
                    }}
                    sx={listRowSx}
                  >
                    <Language sx={{ fontSize: 22, color: DesignSystem.colors.primary }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: DesignSystem.colors.primary }}>
                      {t('openInMaps')}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {contactLinks.length === 0 && !(business.addresses && business.addresses[0]) && (
              <Typography sx={{ px: 2, py: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
                {t('noContacts')}
              </Typography>
            )}
          </Box>

          {(relatedBusinesses.length > 0 || samePersonBusinesses.length > 0) && (
          <Box sx={{ width: '100%', mb: 4 }}>
            <Tabs
              value={
                relatedTab === 'recommended' && relatedBusinesses.length === 0
                  ? 'same_person'
                  : relatedTab === 'same_person' && samePersonBusinesses.length === 0
                    ? 'recommended'
                    : relatedTab
              }
              onChange={(_e, newValue) => setRelatedTab(newValue)}
              sx={{
                mb: 2,
                minHeight: 'auto',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  minHeight: 40,
                  px: 0.5,
                  mr: 2,
                  color: DesignSystem.colors.textSecondary,
                  '&.Mui-selected': { color: DesignSystem.colors.textPrimary },
                },
                '& .MuiTabs-indicator': { display: 'none' },
              }}
            >
              {relatedBusinesses.length > 0 && <Tab value="recommended" label={t('tabRecommended')} />}
              {samePersonBusinesses.length > 0 && <Tab value="same_person" label={t('tabSamePerson')} />}
            </Tabs>

            {(() => {
              const activeRelated =
                relatedTab === 'recommended' && relatedBusinesses.length === 0
                  ? 'same_person'
                  : relatedTab === 'same_person' && samePersonBusinesses.length === 0
                    ? 'recommended'
                    : relatedTab;
              const list = activeRelated === 'recommended' ? relatedBusinesses : samePersonBusinesses;
              return (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                  {list.map((related) => {
                    const relatedUsername = related.username?.replace('@', '') || related.id.toString();
                    return (
                      <Box
                        key={related.id}
                        onClick={() => navigate(`/${relatedUsername}`)}
                        sx={{
                          cursor: 'pointer',
                          p: 2,
                          borderRadius: DesignSystem.borderRadius.lg,
                          bgcolor: DesignSystem.colors.surface,
                          border: 0,
                          minWidth: 120,
                          textAlign: 'center',
                          '&:hover': { bgcolor: DesignSystem.colors.surfaceElevated },
                        }}
                      >
                        <Avatar
                          src={getBusinessLogoUrl(related.logo) || ''}
                          sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: DesignSystem.colors.surfaceElevated }}
                        >
                          {related.name.charAt(0)}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: DesignSystem.colors.textPrimary }}>
                          {related.name}
                        </Typography>
                        {related.category && (
                          <Typography variant="caption" sx={{ color: DesignSystem.colors.textSecondary }}>
                            {related.category.name}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}
          </Box>
          )}

        </Box>
      </Box>

      <Dialog
        open={Boolean(contactSheet)}
        onClose={() => {
          setContactSheet(null);
          setCopyHint(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: DesignSystem.colors.surface,
            backgroundImage: 'none',
            borderRadius: DesignSystem.borderRadius.lg,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {contactSheet?.title}
          {contactSheet?.subtitle && (
            <Typography sx={{ mt: 0.5, fontSize: 14, color: DesignSystem.colors.textSecondary, fontWeight: 400 }}>
              {contactSheet.subtitle}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 1 }}>
          {copyHint && (
            <Alert severity="success" sx={{ mx: 2, mb: 1 }}>
              {t('copied')}
            </Alert>
          )}
          <List disablePadding>
            {(contactSheet?.actions || []).map((action) => (
              <ListItemButton
                key={action.id}
                onClick={async () => {
                  if (action.copyText) {
                    try {
                      await navigator.clipboard.writeText(action.copyText);
                      setCopyHint(true);
                      setTimeout(() => {
                        setContactSheet(null);
                        setCopyHint(false);
                      }, 700);
                    } catch {
                      setContactSheet(null);
                    }
                    return;
                  }
                  if (action.href) {
                    setContactSheet(null);
                    // External https links: new tab; tel/sms/mailto/app schemes: same-window for iOS prompts
                    if (/^https?:\/\//i.test(action.href) && !action.href.includes('wa.me') && !action.href.includes('signal.me')) {
                      window.open(action.href, '_blank', 'noopener,noreferrer');
                    } else {
                      openHref(action.href);
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: DesignSystem.colors.primary }}>
                  {actionIcon(action.id)}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{ fontWeight: 600, color: DesignSystem.colors.textPrimary }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setContactSheet(null);
              setCopyHint(false);
            }}
          >
            {t('closeAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={videoOpen} onClose={() => setVideoOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{business.name}</DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {videoUrl && !videoError && (
            <Box
              component="video"
              src={videoUrl}
              controls
              autoPlay
              playsInline
              onError={() => setVideoError(true)}
              sx={{ width: '100%', maxHeight: '80vh', display: 'block', bgcolor: '#000' }}
            />
          )}
          {videoUrl && videoError && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                This browser cannot play the profile video inline. Open it instead.
              </Alert>
              <Button component="a" href={videoUrl} target="_blank" rel="noopener noreferrer" variant="contained">
                {t('playVideo')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {videoUrl && (
            <Button component="a" href={videoUrl} target="_blank" rel="noopener noreferrer">
              {t('playVideo')}
            </Button>
          )}
          <Button onClick={() => setVideoOpen(false)}>{t('done')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('reportVisi')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, overflow: 'visible' }}>
          {reportErr && <Alert severity="error">{reportErr}</Alert>}
          {reportOk && <Alert severity="success">{reportOk}</Alert>}
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <InputLabel id="report-reason-label" shrink={Boolean(reasonId)}>
              {t('deleteAccountReasonLabel')}
            </InputLabel>
            <Select
              labelId="report-reason-label"
              label={t('deleteAccountReasonLabel')}
              notched={Boolean(reasonId)}
              displayEmpty
              value={reasonId}
              onChange={(e) => setReasonId(e.target.value)}
            >
              {reasons.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('deleteAccountMessageLabel')}
            multiline
            minRows={3}
            value={reportMsg}
            onChange={(e) => setReportMsg(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={actionBusy || !reasonId || !business}
            onClick={async () => {
              if (!user?.token || !business) return;
              setActionBusy(true);
              setReportErr(null);
              try {
                const res = await reportBusiness(
                  business.id,
                  parseInt(reasonId, 10),
                  reportMsg,
                  user.token,
                  getApiLanguage()
                );
                setReportOk(res.message || res.title || t('reported'));
                setTimeout(() => setReportOpen(false), 1200);
              } catch (e) {
                const msg = e instanceof Error ? e.message : t('reportFailed');
                if (/already reported/i.test(msg)) {
                  setReportOk(msg);
                  setTimeout(() => setReportOpen(false), 1200);
                } else {
                  setReportErr(msg);
                }
              } finally {
                setActionBusy(false);
              }
            }}
          >
            {t('submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderOpen} onClose={() => setFolderOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('addToList')}</DialogTitle>
        <DialogContent>
          {folderErr && <Alert severity="error" sx={{ mb: 1 }}>{folderErr}</Alert>}
          {folderOk && <Alert severity="success" sx={{ mb: 1 }}>{folderOk}</Alert>}
          {folders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('folderNoListsYet')}
            </Typography>
          ) : (
            <List>
              {folders.map((f, i) => (
                <ListItemButton
                  key={f.id}
                  disabled={actionBusy || !business}
                  onClick={async () => {
                    if (!user?.token || !business) return;
                    setActionBusy(true);
                    setFolderErr(null);
                    try {
                      await addBusinessToFolder(business.id, f.id, user.token, getApiLanguage());
                      setIsBookmarked(true);
                      setFolderOk(t('addedToList', { name: displayFolderName(f.name, i) }));
                      setTimeout(() => setFolderOpen(false), 900);
                    } catch (e) {
                      setFolderErr(e instanceof Error ? e.message : t('couldNotAdd'));
                    } finally {
                      setActionBusy(false);
                    }
                  }}
                >
                  <ListItemText primary={displayFolderName(f.name, i)} secondary={f.business_count != null ? t('listItemsCount', { n: f.business_count }) : undefined} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderOpen(false)}>{t('close')}</Button>
          <Button onClick={() => { setFolderOpen(false); navigate('/bookmarks'); }}>{t('manageLists')}</Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
